/**
 * SoilCalibrationWizard — Feature 8 UI surface.
 *
 * Per-zone FC/PWP/PAW calibration. Single-page scrollable layout (NOT a
 * multi-route wizard) so users can scroll back to verify what they typed
 * without losing focus. Each step lights up green when its API call
 * succeeds; the safety-floor abort surfaces inline if the drying tick
 * trips it.
 *
 * State machine mirrors `services/zone_soil_calibration.py:WIZARD_STATES`
 * — backend is authoritative; this page just renders + dispatches steps.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Droplets, Sun, Clock, AlertTriangle, ArrowRight,
  CheckCircle2, RotateCcw,
} from 'lucide-react';
import { API_URL } from '../config/api';

const STEPS = [
  { key: 'bucket_test',  title: 'Step 1 — Measure sprinkler output' },
  { key: 'saturating',   title: 'Step 2 — Saturate to field capacity' },
  { key: 'drying',       title: 'Step 3 — Drying phase (TurfPulse watches)' },
  { key: 'confirm_pwp',  title: 'Step 4 — Confirm wilting point' },
  { key: 'applied',      title: 'Step 5 — Review & apply' },
];

const STATE_ORDER = ['not_started', 'bucket_test', 'saturating',
                      'drying', 'confirm_pwp', 'applied'];
const indexOfState = (s) => STATE_ORDER.indexOf(s);
const isStepComplete = (current, key) => indexOfState(current) >= indexOfState(key);

export default function SoilCalibrationWizard() {
  const { zoneId: zoneIdParam } = useParams();
  const navigate = useNavigate();
  const [zoneId, setZoneId] = useState(zoneIdParam || '');
  const [zones, setZones] = useState([]);              // /api/zones/soil-calibration/all
  const [doc, setDoc] = useState(null);                // current zone wizard doc
  const [saving, setSaving] = useState(false);

  // Step inputs (controlled — let users tweak before submitting)
  const [depthIn, setDepthIn] = useState('0.125');
  const [runMin, setRunMin] = useState('15');
  const [fcInput, setFcInput] = useState('');
  const [pwpInput, setPwpInput] = useState('');
  const [madFrac, setMadFrac] = useState('0.50');
  const [grassType, setGrassType] = useState('cool_season');

  // Load zone list
  useEffect(() => {
    axios.get(`${API_URL}/api/zones/soil-calibration/all`, { withCredentials: true })
      .then(r => setZones(r.data?.zones || []))
      .catch(() => setZones([]));
  }, [saving]);

  // Load current zone doc
  useEffect(() => {
    if (!zoneId) { setDoc(null); return; }
    axios.get(`${API_URL}/api/zones/${zoneId}/soil-calibration`, { withCredentials: true })
      .then(r => {
        setDoc(r.data);
        if (r.data?.fc_pct != null) setFcInput(String(r.data.fc_pct));
        if (r.data?.pwp_pct != null) setPwpInput(String(r.data.pwp_pct));
        else if (r.data?.pwp_candidate != null) setPwpInput(String(r.data.pwp_candidate));
        if (r.data?.bucket_test) {
          setDepthIn(String(r.data.bucket_test.measured_depth_in));
          setRunMin(String(r.data.bucket_test.run_duration_min));
        }
      })
      .catch(() => setDoc(null));
  }, [zoneId, saving]);

  const callStep = async (path, body) => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/zones/${zoneId}/soil-calibration/step/${path}`,
        body, { withCredentials: true },
      );
      setDoc(r.data);
      toast.success('Step saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Step failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset wizard for this zone? Applied calibration on the scheduler will stay until you commit a new one.')) return;
    await axios.delete(`${API_URL}/api/zones/${zoneId}/soil-calibration`, { withCredentials: true });
    toast.success('Wizard reset');
    setSaving(s => !s);
  };

  const state = doc?.state || 'not_started';
  const aborted = state === 'aborted_safety';
  const applied = state === 'applied';
  const computedPrecip = useMemo(() => {
    const d = parseFloat(depthIn); const m = parseFloat(runMin);
    if (!d || !m) return null;
    return ((d / m) * 60).toFixed(3);
  }, [depthIn, runMin]);
  const previewPaw = useMemo(() => {
    const fc = parseFloat(fcInput); const pwp = parseFloat(pwpInput);
    const mad = parseFloat(madFrac);
    if (!fc || !pwp || !mad || fc <= pwp) return null;
    const paw = fc - pwp;
    return {
      paw: paw.toFixed(1),
      min: (fc - paw * mad).toFixed(1),
      max: fc.toFixed(1),
    };
  }, [fcInput, pwpInput, madFrac]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="soil-calibration-wizard">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <header className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-200">← Back</button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Droplets className="w-6 h-6 text-sky-400" />
            Soil Calibration Wizard
          </h1>
        </header>

        {/* Zone picker */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-2">
            Zone
          </label>
          <input
            data-testid="wizard-zone-id"
            value={zoneId}
            onChange={(e) => setZoneId(e.target.value.trim())}
            placeholder="Paste Rachio zone ID — find under Settings → Zones"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono"
          />
          {zones.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {zones.map(z => (
                <button
                  key={z.rachio_zone_id}
                  onClick={() => setZoneId(z.rachio_zone_id)}
                  className={`text-[11px] px-2 py-1 rounded-full border ${
                    z.rachio_zone_id === zoneId
                      ? 'border-emerald-500 bg-emerald-900/40 text-emerald-200'
                      : 'border-slate-700 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  {z.rachio_zone_id.slice(0, 8)}… · {z.state}
                </button>
              ))}
            </div>
          )}
        </div>

        {!zoneId && (
          <div className="text-sm text-slate-400 italic">Pick or paste a zone ID to begin.</div>
        )}

        {zoneId && aborted && (
          <div data-testid="wizard-abort" className="bg-rose-950/40 border border-rose-700/60 rounded-xl p-4 text-rose-200">
            <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-5 h-5" /> Calibration aborted</div>
            <div className="text-sm mt-2">{doc?.aborted_reason || 'Safety floor tripped.'}</div>
            <button onClick={reset} className="mt-3 text-xs underline">Reset and re-run</button>
          </div>
        )}

        {zoneId && !aborted && (
          <>
            {/* Step indicator */}
            <ol className="grid grid-cols-5 gap-2">
              {STEPS.map((s, i) => {
                const done = isStepComplete(state, s.key);
                const current = state === s.key || (state === 'drying' && s.key === 'drying');
                return (
                  <li key={s.key}
                      className={`text-[10px] uppercase tracking-widest text-center rounded-md py-2 border ${
                        done ? 'bg-emerald-900/40 border-emerald-700 text-emerald-200'
                             : current ? 'bg-slate-800 border-slate-600 text-slate-100'
                                       : 'bg-slate-900/40 border-slate-800 text-slate-500'
                      }`}>
                    <div className="font-bold">{i + 1}</div>
                  </li>
                );
              })}
            </ol>

            {/* Step 1 */}
            <Section icon={<Clock className="w-5 h-5 text-amber-400" />} title="Step 1 — Sprinkler output"
                     done={isStepComplete(state, 'bucket_test')} testid="step-bucket">
              <p className="text-xs text-slate-400 leading-relaxed mb-3">
                Place a straight-sided container in the spray pattern. Run the zone for exactly 15 minutes.
                Measure how deep the water is. TurfPulse converts that to your sprinkler's in/hr rate.
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <NumField label="Water depth (in)" value={depthIn} onChange={setDepthIn} />
                <NumField label="Run duration (min)" value={runMin} onChange={setRunMin} />
              </div>
              {computedPrecip && (
                <div className="text-[11px] text-emerald-400 mt-2">Calculated: <span className="font-mono">{computedPrecip} in/hr</span></div>
              )}
              <button
                data-testid="step-bucket-submit"
                disabled={saving}
                onClick={() => callStep('bucket-test', { measured_depth_in: parseFloat(depthIn), run_duration_min: parseInt(runMin, 10) })}
                className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 rounded-md text-xs font-semibold">
                Save Step 1
              </button>
            </Section>

            {/* Step 2 */}
            <Section icon={<Droplets className="w-5 h-5 text-sky-400" />} title="Step 2 — Saturate to field capacity"
                     done={isStepComplete(state, 'saturating')} testid="step-saturate">
              <p className="text-xs text-slate-400 leading-relaxed mb-3">
                Run this zone for 45–60 minutes to soak the soil. Wait 24 hours for gravity to drain the excess.
                Then read the live sensor value below — that's your field capacity (FC).
              </p>
              <NumField label="FC reading (% VWC)" value={fcInput} onChange={setFcInput} placeholder="e.g. 58" />
              <button
                data-testid="step-saturate-submit"
                disabled={saving || !fcInput}
                onClick={() => callStep('saturate', { fc_pct: parseFloat(fcInput) })}
                className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 rounded-md text-xs font-semibold">
                Save FC and start drying
              </button>
            </Section>

            {/* Step 3 */}
            <Section icon={<Sun className="w-5 h-5 text-orange-400" />} title="Step 3 — Drying phase (TurfPulse watches)"
                     done={isStepComplete(state, 'confirm_pwp')} testid="step-drying">
              <p className="text-xs text-slate-400 leading-relaxed">
                Don't water this zone. TurfPulse polls your probe every 4 hours. When the curve flattens
                (less than 1% change over 12h), that's your permanent wilting point. We'll push you a
                notification when it's ready — usually 3–7 days.
              </p>
              {state === 'drying' && (
                <div className="mt-3 text-[11px] bg-slate-800/60 rounded-md p-3">
                  <div className="font-semibold text-slate-200">Drying phase active</div>
                  <div className="text-slate-400 mt-1">
                    Samples logged: <span className="font-mono">{doc?.drying_readings?.length || 0}</span>
                    {doc?.pwp_candidate != null && (
                      <span className="ml-3 text-emerald-400">Plateau candidate: <span className="font-mono">{doc.pwp_candidate}%</span></span>
                    )}
                  </div>
                  <div className="text-slate-500 mt-1">
                    Safety floor: <span className="font-mono">15%</span> · we abort and notify you before lawn damage.
                  </div>
                </div>
              )}
            </Section>

            {/* Step 4 */}
            <Section icon={<ShieldCheck className="w-5 h-5 text-yellow-400" />} title="Step 4 — Confirm wilting point"
                     done={isStepComplete(state, 'confirm_pwp')} testid="step-confirm-pwp">
              <p className="text-xs text-slate-400 leading-relaxed mb-3">
                Override the detected plateau if you saw earlier wilting signs.
              </p>
              <NumField label="PWP (% VWC)" value={pwpInput} onChange={setPwpInput} placeholder="e.g. 22" />
              <button
                data-testid="step-confirm-pwp-submit"
                disabled={saving || !pwpInput}
                onClick={() => callStep('confirm-pwp', { pwp_pct: parseFloat(pwpInput) })}
                className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 rounded-md text-xs font-semibold">
                Confirm PWP
              </button>
            </Section>

            {/* Step 5 */}
            <Section icon={<CheckCircle2 className="w-5 h-5 text-emerald-400" />} title="Step 5 — Review & apply"
                     done={applied} testid="step-commit">
              <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                <SelectField label="Grass type" value={grassType} onChange={setGrassType}
                              options={[
                                { v: 'cool_season', l: 'Cool season' },
                                { v: 'warm_season_bermuda', l: 'Warm season (Bermuda)' },
                              ]} />
                <NumField label="MAD fraction" value={madFrac} onChange={setMadFrac} step="0.05" />
              </div>
              {previewPaw && (
                <div className="bg-slate-800/60 rounded-md p-3 text-[11px] font-mono text-slate-200">
                  PAW: {previewPaw.paw}%  ·  MIN: {previewPaw.min}%  ·  MAX: {previewPaw.max}%
                </div>
              )}
              <button
                data-testid="step-commit-submit"
                disabled={saving || !previewPaw}
                onClick={() => callStep('commit', { mad_fraction: parseFloat(madFrac), grass_type: grassType })}
                className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1">
                Apply calibration <ArrowRight className="w-3 h-3" />
              </button>
              {applied && (
                <div data-testid="wizard-applied" className="mt-3 bg-emerald-950/40 border border-emerald-700/60 rounded-md p-3 text-[11px] text-emerald-200">
                  ✓ Calibrated. TurfPulse now uses these targets for this zone — applied at {new Date(doc.applied_at).toLocaleString()}.
                </div>
              )}
            </Section>

            <div className="text-right">
              <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-300 inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Reset wizard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Section = ({ icon, title, done, children, testid }) => (
  <section
    data-testid={testid}
    className={`bg-slate-900/60 border rounded-xl p-4 space-y-2 ${done ? 'border-emerald-800' : 'border-slate-800'}`}
  >
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
      {done && <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Done</span>}
    </div>
    {children}
  </section>
);

const NumField = ({ label, value, onChange, placeholder, step = '0.01' }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
    <input
      type="number" step={step} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-sm font-mono"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
    <select
      value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-sm">
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </label>
);
